// Telefono con «Sito desktop» attivo (Luan 03/11/2026 20:17-20:29: «non va bene che una scelta così comune possa intaccare l'utilizzo
// delle app, risolvi in maniera permanente»). Con quell'opzione Chrome per Android ignora il viewport della pagina e la disegna larga
// almeno 980 px, rimpicciolita: le app escono con l'impaginazione da computer, e il sito non può spegnere l'impostazione del browser.
// Soluzione (regola 14 di CLAUDE.md): prima di caricare qualunque app, main.tsx chiama avviaRiquadroTelefono(). Se la pagina è larga da
// computer su uno schermo da telefono, la stessa app si apre dentro un riquadro (iframe dello stesso indirizzo) largo quanto il telefono
// e ingrandito a tutto schermo: dentro il riquadro la larghezza è quella vera del telefono e l'impaginazione è quella del telefono.
// Stessa origine: stesso accesso, stessi dati, stesse notifiche. L'indirizzo in alto segue quello del riquadro.

interface Finestra { innerWidth: number; screen: { width: number; height: number }; matchMedia?: (q: string) => { matches: boolean } }

/** Schermo da telefono (lato corto ≤ 600 px, tocco) ma pagina disegnata larga come su un computer. */
export function sitoDesktopSuTelefono(w: Finestra): boolean {
  const lato = Math.min(w.screen.width, w.screen.height);
  const tocco = w.matchMedia?.("(pointer: coarse)").matches ?? false;
  return tocco && lato > 0 && lato <= 600 && w.innerWidth >= 800 && w.innerWidth >= lato * 1.5;
}

/** Fattore di ingrandimento del riquadro: larghezza della pagina ÷ larghezza dello schermo (nel verso in cui è tenuto il telefono). */
export function fattoreRiquadro(w: Finestra): number {
  const largo = w.screen.width > 0 ? w.screen.width : Math.min(w.screen.width, w.screen.height);
  return Math.max(1, w.innerWidth / Math.max(1, largo));
}

/** Dentro il riquadro? (pagina incorniciata dalla stessa origine) */
export function inRiquadro(): boolean {
  try { return window.top !== window && window.top?.location.origin === window.location.origin; } catch { return false; }
}

/** App installata (a schermo intero): vale anche dentro il riquadro, dove la modalità è quella della pagina che lo contiene. */
export function appInstallata(): boolean {
  const qui = (w: Window) => w.matchMedia?.("(display-mode: standalone)").matches || (w.navigator as { standalone?: boolean }).standalone === true;
  try { return qui(window) || (inRiquadro() && qui(window.top as Window)); } catch { return qui(window); }
}

/**
 * Da chiamare per prima cosa, prima di caricare l'app. true = telefono con «Sito desktop»: la pagina diventa la cornice del riquadro
 * e non va caricata l'app qui (la carica il riquadro).
 */
export function avviaRiquadroTelefono(doc: Document = document, w: Window = window): boolean {
  if (w.top !== w || !sitoDesktopSuTelefono(w)) return false;
  const f = doc.createElement("iframe");
  f.src = w.location.pathname + w.location.search + w.location.hash;
  f.title = doc.title || "QBI";
  f.setAttribute("allow", "camera; microphone; geolocation; clipboard-read; clipboard-write; fullscreen; web-share; screen-wake-lock; autoplay");
  f.setAttribute("allowfullscreen", "");
  for (const el of [doc.documentElement, doc.body]) Object.assign(el.style, { margin: "0", padding: "0", height: "100%", overflow: "hidden", background: "#fff" });
  Object.assign(f.style, { position: "fixed", top: "0", left: "0", border: "0", transformOrigin: "0 0", display: "block" });
  const disponi = () => {
    const k = fattoreRiquadro(w);
    f.style.width = `${w.innerWidth / k}px`;
    f.style.height = `${w.innerHeight / k}px`;
    f.style.transform = `scale(${k})`;
  };
  disponi();
  w.addEventListener("resize", disponi);
  w.addEventListener("orientationchange", () => setTimeout(disponi, 300));
  // l'indirizzo e il titolo della pagina seguono il riquadro (ricarica, condivisione del link, cronologia)
  f.addEventListener("load", () => {
    try {
      const dentro = f.contentWindow!;
      const segui = () => {
        const u = dentro.location.pathname + dentro.location.search + dentro.location.hash;
        if (u !== w.location.pathname + w.location.search + w.location.hash) w.history.replaceState(null, "", u);
        if (dentro.document.title) doc.title = dentro.document.title;
      };
      segui();
      dentro.addEventListener("hashchange", segui);
      dentro.addEventListener("popstate", segui);
      new MutationObserver(segui).observe(dentro.document.head, { childList: true, subtree: true, characterData: true });
    } catch { /* stessa origine: non dovrebbe succedere */ }
  });
  doc.getElementById("root")?.remove();
  doc.body.appendChild(f);
  return true;
}
